import type { AgentHealth } from '@tabel/shared/connector/protocol';
import { AGENTS } from '@tabel/shared/connector/versions';
import type { ConnectorView } from '@tabel/shared/schemas/agent-client';
import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { AGENT_STATE_LABEL, LOGIN_LABEL } from '../../pages/MyAgents/texts';
import { Dropdown, Modal } from '../ui';
import { LABEL, T, TITLE, agentName, say } from './texts';
import type { AgentChoice } from './useThread';
import { usePhone } from './viewport';

/*
 * Выбор агента в шапке «Помощника» (contracts/ui.md §1; мокап Main.dc.html, «агент ▾»): агенты своих компьютеров,
 * у каждого — состояние и вход («Готов · подписка Claude»). Агента выбирают в новом разговоре: в разговоре с
 * сообщениями другие агенты недоступны. На телефоне список — нижним листом (FR-039).
 */

function sub(h: AgentHealth): string {
  const login =
    h.loginMethod === 'subscription'
      ? LOGIN_LABEL.subscription[h.agent]
      : h.loginMethod
        ? LOGIN_LABEL[h.loginMethod]
        : null;
  return [AGENT_STATE_LABEL[h.state], login].filter(Boolean).join(' · ');
}

interface Option {
  key: string;
  choice: AgentChoice;
  name: string;
  sub: string;
  ready: boolean;
}

function options(list: readonly ConnectorView[]): { pc: ConnectorView; items: Option[] }[] {
  return list
    .filter((c) => c.phase === 'active')
    .map((pc) => ({
      pc,
      items: [...pc.agents]
        .sort((a, b) => AGENTS.indexOf(a.agent) - AGENTS.indexOf(b.agent))
        .map((h) => ({
          key: `${pc.id}:${h.agent}`,
          choice: { connectorId: pc.id, agent: h.agent },
          name: agentName(h.agent),
          sub: sub(h),
          ready: h.state === 'ready',
        })),
    }));
}

export function AgentMenu({
  computers,
  value,
  locked,
  onPick,
}: {
  computers: readonly ConnectorView[];
  value: AgentChoice | null;
  /**
   * В разговоре уже есть сообщения: агент этого разговора не меняется. Выбор другого агента — новый разговор с ним
   * (приёмка 18.10.2026: закрытые пункты меню выглядели как «переключить не даёт»); прежний остаётся в списке.
   */
  locked: boolean;
  onPick: (c: AgentChoice) => void;
}) {
  const phone = usePhone();
  const [sheet, setSheet] = useState(false);
  const groups = options(computers);
  const current = groups
    .flatMap((g) => g.items)
    .find((o) => o.key === `${value?.connectorId}:${value?.agent}`);
  const pc = computers.find((c) => c.id === value?.connectorId) ?? null;
  const many = groups.length > 1;
  const foot = [pc ? say.computerLine(pc.name, pc.online) : null, locked ? T.agentInNew : T.agentsPage]
    .filter(Boolean)
    .join(' ');

  const trigger = (open: boolean, toggle: () => void) => (
    <button
      type="button"
      aria-haspopup={phone ? 'dialog' : 'menu'}
      aria-expanded={open}
      aria-label={current ? `${LABEL.chooseAgent}: ${current.name}` : LABEL.chooseAgent}
      title={LABEL.chooseAgent}
      onClick={toggle}
      className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-control px-2 text-[12.5px] font-medium text-ink-2 transition-colors hover:bg-subtle hover:text-ink max-tablet:min-h-10"
    >
      <span
        aria-hidden
        className={`h-[7px] w-[7px] shrink-0 rounded-full ${current?.ready && pc?.online ? 'bg-ok' : 'bg-warn'}`}
      />
      <span className="max-w-[120px] truncate">{current?.name ?? TITLE.agent}</span>
      <ChevronDown size={16} strokeWidth={1.75} aria-hidden />
    </button>
  );

  const list = (close: () => void) => (
    <>
      {groups.map((g) => (
        <div key={g.pc.id} role="group" aria-label={many ? g.pc.name : TITLE.agent}>
          <p
            aria-hidden
            className="px-3 pt-1.5 pb-1 text-[11px] leading-[1.4] font-semibold tracking-[0.06em] text-ink-3 uppercase"
          >
            {many ? g.pc.name : TITLE.agent}
          </p>
          {g.items.map((o) => {
            const on = o.key === current?.key;
            return (
              <button
                key={o.key}
                type="button"
                role="menuitem"
                aria-current={on ? 'true' : undefined}
                onClick={() => {
                  if (!on) onPick(o.choice);
                  close();
                }}
                className="flex w-full items-start gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-subtle focus:bg-subtle focus:outline-none disabled:cursor-default disabled:hover:bg-transparent max-sm:min-h-10"
              >
                <span className="min-w-0 flex-1 leading-[1.35]">
                  <span className="block text-[13.5px] text-ink">{o.name}</span>
                  <span className="mt-0.5 block text-[12px] text-ink-2">{o.sub}</span>
                </span>
                {on && (
                  <Check
                    size={16}
                    strokeWidth={1.75}
                    className="mt-0.5 shrink-0 text-ink"
                    aria-label={LABEL.selected}
                  />
                )}
              </button>
            );
          })}
        </div>
      ))}
      <hr className="my-1 border-line" />
      <p className="px-3 pt-1 pb-1.5 text-[12px] leading-[1.5] text-ink-2">{foot}</p>
    </>
  );

  if (phone)
    return (
      <>
        {trigger(sheet, () => setSheet((v) => !v))}
        <Modal open={sheet} onClose={() => setSheet(false)} title={LABEL.chooseAgent}>
          <div role="menu" aria-label={TITLE.agent} className="-mx-3">
            {list(() => setSheet(false))}
          </div>
        </Modal>
      </>
    );
  return (
    <Dropdown align="end" width="w-[300px]" trigger={({ open, toggle }) => trigger(open, toggle)}>
      {list}
    </Dropdown>
  );
}
